import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  applySiteSettingsToDocument,
  getSystemColorScheme,
  loadSiteSettings,
  saveSiteSettings,
  subscribeSystemColorScheme,
  type SiteSettings,
  type ThemePreference,
} from '@/utils/siteSettings';
const SiteSettingsPanel = lazy(() => import('@/components/SiteSettingsPanel').then((mod) => ({ default: mod.SiteSettingsPanel })));

interface SiteSettingsContextValue {
  settings: SiteSettings;
  resolvedTheme: 'light' | 'dark';
  setTheme: (t: ThemePreference) => void;
  setReducedTransparency: (v: boolean) => void;
  setReducedMotion: (v: boolean) => void;
  openSettings: () => void;
  closeSettings: () => void;
  settingsOpen: boolean;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | null>(null);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(() => loadSiteSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The OS scheme is React state, so under "Match device" resolvedTheme (and
  // everything keyed on it) updates when the OS switches between light and dark.
  const systemScheme = useSyncExternalStore(subscribeSystemColorScheme, getSystemColorScheme);
  const resolvedTheme = settings.theme === 'system' ? systemScheme : settings.theme;

  // Re-applied when the OS scheme flips too. A layout effect, so <html> already
  // carries the new theme when children's effects run: a canvas that redraws on
  // resolvedTheme reads the new colours through cssVarColor.
  useLayoutEffect(() => {
    applySiteSettingsToDocument(settings);
  }, [settings, systemScheme]);

  useEffect(() => {
    saveSiteSettings(settings);
  }, [settings]);

  const setTheme = useCallback((t: ThemePreference) => {
    setSettings(s => ({ ...s, theme: t }));
  }, []);

  const setReducedTransparency = useCallback((v: boolean) => {
    setSettings(s => ({ ...s, reducedTransparency: v }));
  }, []);

  const setReducedMotion = useCallback((v: boolean) => {
    setSettings(s => ({ ...s, reducedMotion: v }));
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(
    () => ({
      settings,
      resolvedTheme,
      setTheme,
      setReducedTransparency,
      setReducedMotion,
      openSettings,
      closeSettings,
      settingsOpen,
    }),
    [
      settings,
      resolvedTheme,
      setTheme,
      setReducedTransparency,
      setReducedMotion,
      openSettings,
      closeSettings,
      settingsOpen,
    ],
  );

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
      {settingsOpen && (
        <Suspense fallback={null}>
          <SiteSettingsPanel open={settingsOpen} onClose={closeSettings} />
        </Suspense>
      )}
    </SiteSettingsContext.Provider>
  );
}

// Paired hook for this context; rule assumes one export kind per file.
// eslint-disable-next-line react-refresh/only-export-components -- context module pattern
export function useSiteSettings(): SiteSettingsContextValue {
  const v = useContext(SiteSettingsContext);
  if (!v) throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  return v;
}
